import { useCallback, useEffect, useRef } from "react";

export interface SSEEvent {
  type: string;
  data: string;
}

// EventSource cannot send custom headers, so we use the Fetch API and parse
// the SSE wire format from the response body stream.
export function useSSEFetch(
  url: string,
  headers: Record<string, string>,
  onEvent: (event: SSEEvent) => void,
  enabled: boolean,
) {
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const abortRef = useRef<AbortController | null>(null);

  const connect = useCallback(
    async function run() {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch(url, {
          headers: { ...headers, Accept: "text/event-stream" },
          signal: controller.signal,
        });

        if (!res.ok || !res.body) return;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split("\n\n");
          buffer = blocks.pop() ?? "";

          for (const block of blocks) {
            const event = parseSSEBlock(block);
            if (event) onEventRef.current(event);
          }
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        // Reconnect after a short delay on unexpected errors
        setTimeout(() => void run(), 3000);
      }
    },
    [url, headers],
  );

  useEffect(() => {
    if (!enabled) return;
    void connect();
    return () => {
      abortRef.current?.abort();
    };
  }, [connect, enabled]);
}

function parseSSEBlock(block: string): SSEEvent | null {
  let type = "message";
  let data = "";

  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) {
      type = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      data = line.slice("data:".length).trim();
    }
  }

  return data ? { type, data } : null;
}
